import React, {
  Children,
  FC,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { CarouselItem } from './CarouselItem';
import {
  CarouselTinderVariants,
  useCarouselTinderStore
} from '../CarouselTinderStore';
import { Wrapper } from './Wrapper';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import { useAudioEffect } from '~/ui/audio-effect/useAudioEffect';
import audioEffectRightAnswer from '~/assets/mp3/right_answer.mp3';
import audioEffectWrongAnswer from '~/assets/mp3/wrong_answer.mp3';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { select } from '~/lib/select';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { shallow } from 'zustand/shallow';
import { CarouselTinderText } from './CarouselTinderText';
import { useQuizStore } from '../../quiz/QuizStore';
import { useEditorStore } from '~/shell/Store';

const mediaSelector = (s: MediaPlayerStore) => select(s, 'reset', 'loadMedia');

interface TinderCardAPI {
  swipe(dir?: string): Promise<void>;
  restoreCard(): Promise<void>;
}

interface TinderCardItem {
  id: string | number;
  feedback?: string;
  feedbackAudios?: Array<{ mediaUrl?: string; subtitlesUrl?: string }>;
}

let preventMultipleAnimationCalls = false;

export const CarouselTinderComponent: FC<{
  variant: CarouselTinderVariants;
  id: string;
}> = ({ variant, children, id }) => {
  const { reset, loadMedia } = useMediaPlayerStore(mediaSelector, shallow);
  const {
    index,
    setTotal,
    setVariant,
    reset: resetIndex
  } = useCarouselTinderStore();
  const childArray = Children.toArray(children);
  const { addBlocker, removeBlocker, blockers, setAnswer, lastAnswer } =
    useSequenceStore();
  const [disabledSwipe, setDisabledSwipe] = useState(false);
  const [playAudioEffectRightAnswer] = useAudioEffect(audioEffectRightAnswer);
  const [playAudioEffectWrongAnswer] = useAudioEffect(audioEffectWrongAnswer);
  const [preventSwipeDown, setPreventSwipeDown] = useState(true);
  const { setQuizFeedback } = useQuizStore();

  const [currentIndex, setCurrentIndex] = useState(childArray.length - 1);
  const [isLoaded, setIsLoaded] = useState(false);
  const currentIndexRef = useRef(currentIndex);

  useEffect(() => setIsLoaded(true), []);

  const childRefs: React.RefObject<TinderCardAPI>[] = useMemo(
    () =>
      Array(childArray.length)
        .fill(0)
        .map(
          () =>
            React.createRef<TinderCardAPI>() as React.RefObject<TinderCardAPI>
        ),
    []
  );

  // If answer is correct from button click handler, we swipe
  useEffect(() => {
    if (
      isLoaded &&
      blockers.length - 1 < currentIndex &&
      lastAnswer === 'correct'
    ) {
      swipe('down');
      setPreventSwipeDown(false);
      setTimeout(() => {
        setPreventSwipeDown(true);
      }, 0);
    }
  }, [blockers, isLoaded, lastAnswer]);

  const updateCurrentIndex = (val: number) => {
    setCurrentIndex(val);
    currentIndexRef.current = val;
  };

  useEffect(() => {
    if (variant === 'standard' || index === childArray.length - 1) return;
    addBlocker(id);
    return () => removeBlocker(id);
  }, [variant, index]);

  useEffect(() => {
    return () => {
      const { editorMode } = useEditorStore.getState();
      if (!editorMode) {
        resetIndex();
      }
    };
  }, []);

  const outOfFrame = (idx: number) =>
    currentIndexRef.current >= idx && childRefs[idx].current!.restoreCard();

  const canSwipe = currentIndex >= 0;

  const swipe = async (dir: string) => {
    if (canSwipe && currentIndex < childArray.length) {
      await childRefs[currentIndex].current!.swipe(dir);
    }
  };

  useGlobalMedia();

  const globalMedia = useGlobalMediaStore();
  const globalPlayer = globalMedia?.player;

  useEffect(
    () => setDisabledSwipe(globalPlayer ? true : false),
    [globalPlayer]
  );

  const swiped = (
    direction: string,
    isLeftAnswerRight: boolean | undefined,
    cardId: string,
    items: TinderCardItem[]
  ) => {
    if (direction === 'down') {
      updateCurrentIndex(currentIndex - 1);
      return;
    }

    const isAnswerRight =
      (isLeftAnswerRight && direction === 'left') ||
      (!isLeftAnswerRight && direction === 'right');

    isAnswerRight && updateCurrentIndex(currentIndex - 1);

    if (preventMultipleAnimationCalls) {
      return;
    }

    preventMultipleAnimationCalls = true;

    setTimeout(() => (preventMultipleAnimationCalls = false), 0);

    const feedbackAudiosCorrect = items[isLeftAnswerRight ? 0 : 1]
      ?.feedbackAudios
      ? {
          mediaUrl:
            items[isLeftAnswerRight ? 0 : 1]?.feedbackAudios![0]?.mediaUrl,
          subtitlesUrl:
            items[isLeftAnswerRight ? 0 : 1]?.feedbackAudios![0]?.subtitlesUrl
        }
      : false;

    const feedbackAudiosWrong = items[isLeftAnswerRight ? 1 : 0]?.feedbackAudios
      ? {
          mediaUrl:
            items[isLeftAnswerRight ? 1 : 0]?.feedbackAudios![0]?.mediaUrl,
          subtitlesUrl:
            items[isLeftAnswerRight ? 1 : 0]?.feedbackAudios![0]?.subtitlesUrl
        }
      : false;

    const feedback = items[isLeftAnswerRight ? 1 : 0].feedback;

    setAnswer(cardId, items[direction === 'left' ? 0 : 1].id as string);

    if (isAnswerRight) {
      setQuizFeedback('');
      if (feedbackAudiosCorrect && feedbackAudiosCorrect.mediaUrl) {
        reset();
        loadMedia(
          feedbackAudiosCorrect.mediaUrl,
          feedbackAudiosCorrect?.subtitlesUrl,
          'audio',
          true,
          0
        );
      }
      playAudioEffectRightAnswer();
      removeBlocker(cardId);
    } else {
      setQuizFeedback(feedback);
      if (feedbackAudiosWrong && feedbackAudiosWrong.mediaUrl) {
        reset();
        loadMedia(
          feedbackAudiosWrong.mediaUrl,
          feedbackAudiosWrong?.subtitlesUrl,
          'audio',
          true,
          0
        );
      }
      playAudioEffectWrongAnswer();
    }
  };

  setTotal(childArray.length);
  setVariant(variant);

  return (
    <Wrapper
      variant={variant}
      p={{ _: 0, md: 'lg' }}
      gap={{ _: 'xs', sm: 'sm', md: 'lg' }}
      aria-live="polite"
    >
      {childArray.map((c, i) => {
        type TinderChildProps = {
          extraItems?: Array<{
            items?: Array<{ values?: { answer?: string } }>;
          }>;
          id?: string;
          items?: TinderCardItem[];
        };
        const child = c as React.ReactElement<TinderChildProps>;
        return (
          <CarouselItem
            variant={variant}
            index={i}
            total={childArray.length}
            ref={
              childRefs[
                childArray.length - 1 - i
              ] as unknown as React.Ref<TinderCardAPI>
            }
            disabledSwipe={disabledSwipe}
            isCurrentCardActive={childArray.length - i - 1 === currentIndex}
            key={i}
            onSwipe={dir =>
              swiped(
                dir,
                child?.props?.extraItems![0]?.items![0]?.values?.answer as
                  boolean | undefined,
                child?.props?.id!,
                child?.props?.items!
              )
            }
            onCardLeftScreen={() => outOfFrame(childArray.length - 1 - i)}
            preventSwipe={['up', `${preventSwipeDown ? 'down' : ''}`]}
          >
            {c}
          </CarouselItem>
        );
      })}
      {currentIndexRef.current < 0 && <CarouselTinderText />}
    </Wrapper>
  );
};
